import React from 'react';
import { useTranslation } from 'react-i18next';
import combineStyles from '../utils/combineStyles';
import { useAppSelector } from '../app/hooks';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import Text from './Text';
import { OrderingSelectors, OrderingHooks } from 'polygon-ordering';
import Energy from './Energy';
import AllergenTagList from './AllergenTagList';
import { TEXT_PROPERTIES } from '../utils/theme';
import getThemeLookup from '../selectors/getThemeLookup';

const { getStockBalanceDataByItem, getOpenPurchase } = OrderingSelectors;
const { useFormattedCurrency } = OrderingHooks;

const MenuItemDescriptions = ({
  item,
  availabilityTimeString = '',
  imagePath,
}: {
  item: Item;
  availabilityTimeString: string;
  imagePath?: string;
}) => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);

  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);

  const stockBalanceData = useAppSelector(
    getStockBalanceDataByItem(item, useAppSelector(getOpenPurchase) as PurchaseWithTotals),
  );

  const checkIfDisabled =
    stockBalanceData && (stockBalanceData.soldOut || stockBalanceData.soldOutByCartSelections);

  const multiSized = !!item.sizes.length;
  const hasRequiredPaidChoices = item.choiceSets.some(
    choiceSet => choiceSet.min && choiceSet.choices.some(choice => choice.baseMoneyPrice),
  );
  const priceShowing = multiSized ? item.minSizeBaseMoneyPrice : item.baseMoneyPrice;
  const price = useFormattedCurrency({ cents: priceShowing });
  const priceString = priceShowing
    ? `${multiSized || hasRequiredPaidChoices ? 'From ' : ''}${price}`
    : null;

  const kilojoulesShowing = multiSized ? item.minSizeKilojoules : item.kilojoules;
  const defaultItem = item.sizes.find(s => s.id === item.default_size) ?? item;
  return (
    <div
      style={combineStyles(
        { height: '100%', flexGrow: '1' },
        Boolean(checkIfDisabled) && styles.menuItemOpacity,
      )}
    >
      <div
        style={{ marginTop: deviceTypeMobile ? 0 : 10, marginBottom: deviceTypeMobile ? 5 : 16 }}
      >
        <Text
          themeKey="menuListItemName"
          style={combineStyles(p('menuItemName', TEXT_PROPERTIES), { marginBottom: 0 })}
          value={item.name}
          variant="h3"
          lines={1}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          // marginTop: 10,
          width: '100%',
        }}
      >
        {!!priceShowing && (
          <Text themeKey="menuListItemPrice" style={p('menuItemPrice', TEXT_PROPERTIES)}>
            {priceString}
          </Text>
        )}

        {!!kilojoulesShowing && <Energy kilojouleCount={kilojoulesShowing} />}
      </div>

      <div>
        {item.description ? (
          <Text
            themeKey="menuListItemDescriptionText"
            value={item.description}
            style={combineStyles(
              p('menuItemDescription', TEXT_PROPERTIES),
              deviceTypeMobile ? styles.mobileDescription : styles.description,
            )}
            lines={item.tags?.length ? 2 : 3}
          />
        ) : (
          <div style={deviceTypeMobile ? styles.mobileDescription : styles.description}></div>
        )}
      </div>

      <div>
        {stockBalanceData && stockBalanceData.stockBalanceThreshold && (
          <Text themeKey={stockBalanceData.stockBalanceThreshold} style={styles.stockBalanceText}>
            {t('stockBalanceThreshold.' + stockBalanceData.stockBalanceThreshold)}
          </Text>
        )}
      </div>
      {availabilityTimeString && availabilityTimeString !== '' && (
        <div>
          <Text lines={1} themeKey="menuItemDescription" style={styles.itemAvailable}>
            {availabilityTimeString}
          </Text>
        </div>
      )}
      {!!defaultItem.tags && (
        <AllergenTagList
          includeTempTags={false}
          item={defaultItem}
          hideIcons={deviceTypeMobile && !!imagePath}
        />
      )}
    </div>
  );
};

export default MenuItemDescriptions;

const styles: Styles = {
  itemAvailable: {
    fontSize: 12,
  },
  menuItemOpacity: {
    opacity: 0.5,
  },
  description: {
    marginTop: 10,
    textOverflow: 'ellipsis',
    // marginBottom: 0,
  },
  mobileDescription: {
    marginTop: 5,
    // marginBottom: 0,
  },
  energySuffix: {
    marginLeft: 1,
  },
  stockBalanceText: {
    marginTop: 18,
  },
};
